import { i18n } from './index'

/**
 * Turns a menu label coming from the database into an i18n key fragment.
 *
 * The backend stores `menus.label` as plain text (no translation table), so the
 * agreed contract is: slugify the label and look for `menu.<slug>` in the
 * dictionaries. "Incoming attacks" -> "incoming_attacks".
 */
export function menuLabelToKey(label: string): string {
  return (
    label
      .normalize('NFD')
      // Strip combining marks so "Difesa" and "Difésa" collapse to the same key.
      .replace(/\p{Diacritic}/gu, '')
      .trim()
      // The real data stores labels camelCased ("classificaGiocatori"), so split
      // on the case boundary to get a readable key ("classifica_giocatori").
      .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
  )
}

/**
 * Translated label, falling back to the raw backend text when we have no
 * translation for it. That fallback is deliberate: an untranslated menu entry
 * stays readable instead of showing a broken key.
 */
export function translateMenuLabel(label: string): string {
  const slug = menuLabelToKey(label)
  if (!slug) return label

  const key = `menu.${slug}`
  const { t, te } = i18n.global

  // `te` only checks the active locale, so also probe the fallback one.
  if (te(key) || te(key, 'en')) {
    return t(key)
  }
  return label
}
